import { useAuth } from '@/contexts/AuthContext';
import { useAuthLoopPrevention } from '@/hooks/useAuthLoopPrevention';
import React, { useEffect, useState } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, signIn } = useAuth();
  const oidcAuth = useOidcAuth();
  const [hasTriedAuth, setHasTriedAuth] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  // Use loop prevention hook
  const { hasDetectedLoop, resetLoop } = useAuthLoopPrevention(
    isAuthenticated,
    isLoading,
    oidcAuth.error,
    {
      maxAttempts: 3,
      timeWindow: 30000,
      onLoopDetected: () => {
        console.error('Authentication loop detected in ProtectedRoute');
        setAuthError('Authentication loop detected. Please clear your browser data and try again.');
      }
    }
  );

  // Only show loading overlay after a delay to prevent flashing
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (isLoading) {
      // Only show overlay if loading takes more than 500ms
      timer = setTimeout(() => {
        setShowLoadingOverlay(true);
      }, 500);
    } else {
      setShowLoadingOverlay(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    // Don't proceed if loop detected
    if (hasDetectedLoop) {
      return;
    }

    // Check for authentication errors
    if (oidcAuth.error) {
      console.error('OIDC Authentication error:', oidcAuth.error);
      setAuthError(oidcAuth.error.message || 'Authentication failed');
      return;
    }

    // If not loading, not authenticated, and haven't tried auth yet
    if (!isLoading && !isAuthenticated && !hasTriedAuth) {
      setHasTriedAuth(true);
      signIn();
    }
  }, [isAuthenticated, isLoading, signIn, hasTriedAuth, oidcAuth.error, hasDetectedLoop]);

  // Show authentication error or loop detection
  if (authError || hasDetectedLoop) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ marginBottom: '16px' }}>Authentication Error</h2>
          <p style={{ marginBottom: '20px', opacity: 0.9 }}>
            {hasDetectedLoop
              ? 'Authentication loop detected. Please clear your browser data and try again.'
              : authError
            }
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => {
                setAuthError(null);
                setHasTriedAuth(false);
                resetLoop();
                window.location.reload();
              }}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
            <button
              onClick={() => {
                sessionStorage.clear();
                localStorage.clear();
                window.location.href = '/';
              }}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Clear Session
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading spinner only after delay and if still loading
  if (isLoading && showLoadingOverlay) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ fontSize: '18px', margin: 0 }}>Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show loading while redirecting to login (only if we haven't tried auth yet)
  if (!isAuthenticated && !hasTriedAuth) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ fontSize: '18px', margin: 0 }}>Redirecting to sign in...</p>
        </div>
      </div>
    );
  }

  // If we've tried auth but still not authenticated, show error
  if (!isAuthenticated && hasTriedAuth) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
          <h2 style={{ marginBottom: '16px' }}>Authentication Required</h2>
          <p style={{ marginBottom: '20px', opacity: 0.9 }}>
            Unable to complete authentication. Please try again or contact support.
          </p>
          <button
            onClick={() => {
              setHasTriedAuth(false);
              signIn();
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Render protected content
  return <>{children}</>;
};

export default ProtectedRoute;